// O cartão do site em cima do campo de texto (desenho da tela de 27/10/2026, §4): do texto do campo sai o link
// assentado (`useLinkAssentado`: 600 ms sem tecla, ou espaço, Enter ou colar na hora) e dele a barra, em três
// estados: `buscando` (o pedido saiu), `pronto` (o site respondeu com título) e `casa` (link do próprio SE_WEB,
// sem rede). Em qualquer outro caso a barra NÃO existe: sem link assentado, `escondida` (anexo em preparo), com
// usuário e senha, endereço recusado pelo servidor (`normalizado` nulo), link do SE_WEB que não é tela do app (o
// arquivo assinado `/api/...`, `/health`, um estático), o ✕ já dado nesse link (`semCartaoDe`), o interruptor
// desligado ou ainda não sabido, o site sem título ou fora do ar.
//
// 🔴 O componente de FORA não chama hook de query: as suítes antigas do composer do chat, do atendimento e do
// portal renderizam sem `QueryClientProvider`, e um campo sem link tem de continuar assim. Só com um link
// assentado e visível se monta o interno, que chama `useCartoesLigados` e `usePreviaDeLink` SEMPRE (sem
// condicional: regra dos hooks).
// 🔴 A barra DESMONTA (devolve `null`) quando deve sumir, nunca fica escondida por CSS. E a barra "Buscando…" só
// monta quando o link assenta (nunca por tecla): o `useLinkAssentado` fica de fora, no componente de cima.
// 🔴 Interruptor desligado, ou ainda não sabido (`null`): nem barra, nem pedido (Review Focus 5). `null` é "ainda
// não sei", nunca "desligado para sempre": a barra vem quando a configuração chega.
//
// `faixa` são as classes de um invólucro que só existe com a barra à vista: o atendimento da equipe tem uma faixa
// (`border-t bg-card px-3 pt-2`) que, sem a barra, ficaria vazia. Por isso o invólucro é do interno, que é quem sabe
// se a barra aparece.
import { useCartoesLigados } from "@/hooks/useCartoesLigados";
import { useLinkAssentado } from "@/hooks/useLinkAssentado";
import { usePreviaDeLink } from "@/hooks/usePreviaDeLink";
import { dominioDe, mesmoLink, type PrimeiroLink } from "@/lib/links/links";
import { ehTelaDoLink, previaDaCasa, previaDoSite } from "@/lib/links/previaDoCartao";
import type { OrigemDasPrevias } from "@/services/previasDeLinkApi";

import { CartaoDeLinkEmPreparo } from "./CartaoDeLinkEmPreparo";

interface Props {
  /** O texto do campo (no post, o HTML do editor). */
  texto: string;
  /** Como tirar o primeiro link do texto; o padrão é o `primeiroLink` do texto puro (o post passa o do HTML). */
  extrair?: (texto: string) => PrimeiroLink | null;
  origem: OrigemDasPrevias;
  /** Anexo em preparo (chat, atendimento) ou imagem no corpo (post): a mensagem sai sem cartão, a barra se esconde. */
  escondida?: boolean;
  /** O link (normalizado) cujo ✕ a pessoa já deu; enquanto o primeiro link for esse, não há barra. */
  semCartaoDe: string | null;
  /** O ✕: recebe o link NORMALIZADO, o mesmo que vai no envio como `link_dispensado`. */
  aoTirar: (normalizado: string) => void;
  /** Campo travado (sala fechada, enviando): a barra fica e o ✕ não age. */
  desabilitada?: boolean;
  /** Classes de um invólucro que só existe com a barra visível. */
  faixa?: string;
  /** Classes a mais na barra (o portal passa `mb-0`). */
  className?: string;
}

type PropsDoInterno = Omit<Props, "texto" | "extrair" | "escondida" | "semCartaoDe"> & {
  link: PrimeiroLink;
  /** O `normalizado` do link, já sem o `null` (a guarda do componente de fora). */
  normalizado: string;
};

export function BarraDoCartao({ texto, extrair, escondida, semCartaoDe, ...resto }: Props) {
  const link = useLinkAssentado(texto, extrair);
  if (
    escondida ||
    link === null ||
    link.comCredencial ||
    link.normalizado === null ||
    // Do SE_WEB mas não é tela do app (o arquivo assinado `/api/...`, `/health`, um estático): é arquivo, não
    // página. Sem barra, e nem monta o interno (nenhum pedido).
    (link.casa && !ehTelaDoLink(link)) ||
    mesmoLink(link.normalizado, semCartaoDe)
  ) {
    return null;
  }
  return <BarraAssentada link={link} normalizado={link.normalizado} {...resto} />;
}

function BarraAssentada({
  link,
  normalizado,
  origem,
  aoTirar,
  desabilitada,
  faixa,
  className,
}: PropsDoInterno) {
  // `null` = ainda não se sabe: não consulta e não mostra nada.
  const ligada = useCartoesLigados(origem);
  const { cartao, carregando } = usePreviaDeLink(link, origem, { habilitado: ligada === true });

  if (ligada !== true) return null;

  // Os três estados. Na casa o nome da tela vem do próprio link; fora, o pedido em voo é `buscando` (o domínio já
  // aparece, vem do endereço) e a resposta é `pronto`. O site que falhou, não tem título ou devolveu `null`: a
  // barra some, sem aviso.
  let estado: "buscando" | "pronto" | "casa";
  let dominio: string;
  let titulo: string | undefined;
  let imagemUrl: string | null | undefined;
  if (link.casa) {
    const previa = previaDaCasa(link);
    ({ dominio, titulo } = previa);
    estado = "casa";
  } else if (carregando) {
    estado = "buscando";
    dominio = dominioDe(link.href);
  } else {
    const previa = cartao && previaDoSite(link, cartao);
    if (!previa) return null;
    ({ dominio, titulo, imagem_url: imagemUrl } = previa);
    estado = "pronto";
  }

  const barra = (
    <CartaoDeLinkEmPreparo
      estado={estado}
      url={link.href}
      dominio={dominio}
      titulo={titulo}
      imagemUrl={imagemUrl}
      aoTirar={() => aoTirar(normalizado)}
      desabilitada={desabilitada}
      className={className}
    />
  );
  return faixa ? <div className={faixa}>{barra}</div> : barra;
}
